import styled, { StyledComponent } from '@emotion/styled'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { shade, transparentize } from 'polished'
import React, {
  ButtonHTMLAttributes,
  forwardRef,
  HTMLAttributes,
  useEffect,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { space, SpaceProps } from 'styled-system'

import { Box } from '../Box'
import { Flex } from '../Flex'
import { Spinner } from '../Spinner'
import { Theme } from '../Theme'
import { themeColor, themeFont, themeSpace } from '../Theme/utils'

type BaseButtonProps = {
  loading?: boolean
}

type standardType = { type: 'border' | 'filled' | 'button-main' }
type dropDownType = { type: 'button-dropdown'; options: Options[] }

type ColorVariantProps = {
  variant?: standardType | dropDownType
  bg?: string
  color?: string
}
interface Options {
  id: number
  icon?: string | IconDefinition
  value: string
  label: string
  link?: string
}

const BaseButton = forwardRef<
  HTMLButtonElement,
  BaseButtonProps &
    ColorVariantProps &
    SizeVariantProps &
    HTMLAttributes<HTMLButtonElement>
>(
  (
    {
      loading,
      variant,
      children,
      ...props
    }: BaseButtonProps &
      ColorVariantProps &
      SizeVariantProps &
      HTMLAttributes<HTMLButtonElement>,
    ref,
  ) => {
    const { t } = useTranslation()
    const history = useHistory()
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false)

    return (
      <>
        <ButtonWrapper
          dropdownVisible={dropdownVisible}
          onMouseEnter={() => {
            if (variant?.type === 'button-dropdown') {
              setDropdownVisible(true)
            }
          }}
          onMouseLeave={() => {
            setDropdownVisible(false)
          }}>
          <button
            id={variant?.type === 'button-dropdown' ? 'dropdown-button' : ''}
            {...props}
            ref={ref}>
            {children}
            {loading && <Flex ml={2} />}
            {loading && (
              <Spinner
                size={props.size === 'small' ? 16 : 18}
                color={variant?.type === 'border' ? props.bg : props.color}
              />
            )}
            {variant?.type === 'button-dropdown' && (
              <FontAwesomeIcon icon={'chevron-down'} />
            )}
          </button>
          {variant?.type === 'button-dropdown' &&
            variant?.options &&
            dropdownVisible && (
              <DropDownButtonWrapper>
                {variant.options.map((option, index) => (
                  <DropDownButtonItem
                    key={index}
                    onClick={() => {
                      if (option.link) {
                        history.push(option.link)
                        return
                      }
                      // TODO rewrite after general absences, no time for this atm
                      alert('dropdown should be split from button')
                    }}>
                    <DropDownButtonInsideWrapper>
                      {option.icon && <StyledIcon icon={option.icon} />}
                      {t(`${option.label}`)}
                    </DropDownButtonInsideWrapper>
                  </DropDownButtonItem>
                ))}
              </DropDownButtonWrapper>
            )}
        </ButtonWrapper>
      </>
    )
  },
)
BaseButton.displayName = 'BaseButton'

const ButtonWrapper = styled.div<{ dropdownVisible: boolean }>`
  display: inline-block;
  position: relative;

  #dropdown-button {
    border-bottom-right-radius: ${props => props.dropdownVisible && '0'};
    border-top-right-radius: ${props => props.dropdownVisible && '1.5em'};
    background-color: ${props => props.dropdownVisible && '#005cdb'};
    color: ${props => props.dropdownVisible && '#fff'};
  }
`

const DropDownButtonWrapper = styled.div`
  min-width: 100%;
  width: 170px;
  position: absolute;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  font-size: 0.8em;
  background-color: white;
  z-index: 1;
  border-right: 3px solid #005cdb;
  border-left: 3px solid #005cdb;
  border-bottom: 3px solid #005cdb;
`

const DropDownButtonItem = styled.button`
  border: 0;
  font-weight: normal;
  border-radius: 0;
  width: 100%;
  text-align: right;
  background-color: white;
  color: #005cdb;
  padding: 12px;
  &:hover {
    background-color: #005cdb;
    color: white;
    cursor: pointer
`

const DropDownButtonInsideWrapper = styled.div`
  display: flex;
`

const StyledIcon = styled(FontAwesomeIcon)`
  margin-right: 8px;
`

const colorVariant = (props: ColorVariantProps & { theme: Theme }) => {
  const bg = themeColor(props.bg || '', props.theme)
  const color = themeColor(props.color || '', props.theme)

  const mainButtonStyle = `
   background-color: transparent;
    border: 3px ${bg} solid;
    color: ${bg};
    :hover {
        background-color: ${bg};
        color: ${color};
    }
    :active {
        background-color: ${shade(0.3, bg)};
    }
    :focus {
        outline-color: ${transparentize(0.6, bg)};
    }
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    margin: 0;
  `

  const dropDownButtonStyle = `
    background-color: transparent;
    border: 3px ${bg} solid;
    border-left: 0;
    color: ${bg};
    :hover {
        background-color: ${bg};
        color: ${color};
    }
    :active {
        background-color: ${shade(0.3, bg)};
    }
    :focus {
        outline-color: ${transparentize(0.6, bg)};
    }
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin: 0;
    `

  if (props.variant?.type === 'border')
    return `
    background-color: transparent;
    border: 3px ${bg} solid;
    color: ${bg};
    :hover {
        background-color: ${bg};
        color: ${color};
    }
    :active {
        background-color: ${shade(0.3, bg)};
    }
    :focus {
        outline-color: ${transparentize(0.6, bg)};
    }
    `
  if (props.variant?.type === 'button-main') return mainButtonStyle

  if (props.variant?.type === 'button-dropdown') return dropDownButtonStyle
}

type SizeVariantProps = {
  size?: 'small' | 'large'
}
const sizeVariant = (props: SizeVariantProps & { theme: Theme }) => {
  switch (props.size) {
    case 'small':
      return `
    padding: ${themeSpace(1, props.theme)}px ${themeSpace(2, props.theme)}px;
    font-size: 16px;
    border-radius: 99px;
    font-weight: 600;
        `
    case 'large':
      return `
    padding: ${themeSpace(2, props.theme)}px ${themeSpace(2, props.theme)}px;
    font-size: 18px;
    border-radius: 99px;
    font-weight: 600;
    `
  }
}
export type ButtonProps = SizeVariantProps &
  ColorVariantProps &
  SpaceProps &
  BaseButtonProps

export const Button: StyledComponent<
  ButtonProps,
  ButtonHTMLAttributes<HTMLButtonElement>
> = styled(BaseButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: ${themeFont};
  border: none;
  outline: 3px solid transparent;
  transition: background-color 0.2s ease, color 0.2s ease,
    outline-color 0.2s ease;
  cursor: pointer;

  ${space}
  ${sizeVariant}
  ${colorVariant}
`
Button.defaultProps = {
  bg: 'primary',
  color: 'lights.4',
  size: 'large',
  variant: { type: 'border' },
}

export const EmptyButton = styled(Box.withComponent('button'))`
  background: transparent;
  outline: none;
  border: none;
  cursor: pointer;
`
